import { Grid } from '@mui/material'
import Label from 'components/Label'
import React, { useCallback, useEffect, useState } from 'react'
import { TabTeenagerProps } from '../..'
import FormInput from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import { useInputSelectIdentification } from 'pages/CreateHearing/components/StepIdentification/hooks/select-option.hook'
import { HearingIdentificationType } from 'types/hearing-identification.type'
import { useFormContext } from 'react-hook-form'
import { DocumentList } from 'pages/CreateHearing/components/StepIdentification/components/DocumentList'
import { PhoneList } from 'pages/CreateHearing/components/StepIdentification/components/PhoneList'
import addressService from 'services/address.service'
import { calculateAge } from 'utils/date.util'
import { AddressList } from 'pages/CreateHearing/components/StepIdentification/components/AddressList'

export const TabPersonalData: React.FC<TabTeenagerProps> = () => {
  const inputSelect = useInputSelectIdentification()
  const [naturalnessStateOptions, setNaturalnessStateOptions] = useState<ItemOption[]>([])
  const [naturalnessCityOptions, setNaturalnessCityOptions] = useState<ItemOption[]>([])

  const { control, watch, setValue, setError, clearErrors } =
    useFormContext<HearingIdentificationType>()

  const birthDayValue = watch('dataNascimento')
  const naturalnessStateIdValue = watch('idEstadoNaturalidade')
  const genderIdValue = watch('idGenero')
  const racaIdValue = watch('idRaca')

  const fillNaturalnessCity = useCallback(async (): Promise<void> => {
    try {
      if (!naturalnessStateIdValue || naturalnessStateIdValue === '0') return
      const res = await addressService.getAllCitiesByStateId(naturalnessStateIdValue)
      setNaturalnessCityOptions(res.data)
    } catch (error) {
      console.error('Naturalness city Request: ', error)
    }
  }, [naturalnessStateIdValue])

  const fillNaturalnessState = useCallback(async (): Promise<void> => {
    try {
      const res = await addressService.getAllFullNameStates()
      setNaturalnessStateOptions(res.data)
    } catch (error) {
      console.error('Naturalness state Request: ', error)
    }
  }, [setNaturalnessStateOptions])

  useEffect(() => {
    if (naturalnessStateIdValue === '0') {
      setValue('idMunicipioNaturalidade', '0', { shouldDirty: false })
    } else {
      fillNaturalnessCity()
    }
  }, [naturalnessStateIdValue, fillNaturalnessCity])

  useEffect(() => {
    fillNaturalnessState()
  }, [fillNaturalnessState])

  useEffect(() => {
    if (birthDayValue) {
      setValue('identificacaoAdolescente.idade', calculateAge(birthDayValue))
    }
  }, [birthDayValue, setValue])

  useEffect(() => {
    setValue(
      'descricaoIdGenero',
      inputSelect.genderOptions.find((i) => Number(i.value) === Number(genderIdValue))?.label,
    )
  }, [genderIdValue, inputSelect.genderOptions, setValue])

  useEffect(() => {
    setValue(
      'descricaoIdRaca',
      inputSelect.skinOptions.find((i) => Number(i.value) === Number(racaIdValue))?.label,
    )
  }, [inputSelect.skinOptions, racaIdValue, setValue])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Label text='Identificação' color='primary' v='h6' bold />
      </Grid>
      <Grid item lg={12} md={12} xs={12}>
        <Grid container spacing={2} rowSpacing={4}>
          <Grid item xs={12}>
            <FormInput
              control={control}
              name='nomeSocial'
              label='Nome social'
              placeholder='Informe o Nome social'
              maxLength={60}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              control={control}
              name='nome'
              label='Nome*'
              placeholder='Informe o Nome'
              maxLength={60}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <InputDatePicker control={control} name='dataNascimento' label='Data de nascimento' />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInput
              control={control}
              name='identificacaoAdolescente.idade'
              label='Idade'
              placeholder='Informe a Idade'
              disabled
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idGenero'
              label='Gênero'
              options={inputSelect.genderOptions}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idRaca'
              label='Cor'
              options={inputSelect.skinOptions}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <DocumentList
            control={control}
            watch={watch}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            typeOptions={inputSelect.docTypeOptions}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <AddressList control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <PhoneList control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <Grid item md={12} xs={12}>
            <FormInput
              control={control}
              name='nomeResponsavelAdolescente'
              label='Nome Responsável*'
              placeholder='Informe o Nome do Responsável'
              maxLength={60}
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <FormInput
              control={control}
              name='email'
              label='Email do adolescente/representante legal'
              placeholder='Informe o Email'
              maxLength={60}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <FormInput
              control={control}
              name='nomeMae'
              label='Nome da mãe'
              maxLength={60}
              placeholder='Informe o Nome da mãe'
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInput
              control={control}
              name='nomePai'
              label='Nome do pai'
              maxLength={60}
              placeholder='Informe o Nome do pai'
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idEstadoNaturalidade'
              label='Naturalidade (Estado)'
              options={naturalnessStateOptions}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idMunicipioNaturalidade'
              label='Naturalidade (Município)'
              disabled={naturalnessStateIdValue === '0'}
              options={naturalnessCityOptions}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
